import React, { useState } from "react";
import { Plus, AlertTriangle, Clock } from "lucide-react";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

const COLUMNS = [
  { key: "Backlog",     label: "Backlog",      color: "#ffffff30", border: "border-white/10"          },
  { key: "To Do",       label: "To Do",        color: "#60a5fa",   border: "border-blue-500/25"       },
  { key: "In Progress", label: "In Progress",  color: "#fbbf24",   border: "border-yellow-500/25"     },
  { key: "Review",      label: "Review",       color: "#c084fc",   border: "border-purple-500/25"     },
  { key: "Done",        label: "Done",         color: "#00ff90",   border: "border-[#00ff90]/25"      },
];

const PRI_COLOR = {
  "Critical": "text-red-400 bg-red-500/10 border-red-500/20",
  "High":     "text-orange-400 bg-orange-500/10 border-orange-500/20",
  "Medium":   "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  "Low":      "text-white/30 bg-white/5 border-white/10",
};

function daysUntilDue(due) {
  if (!due) return null;
  return Math.ceil((new Date(due).getTime() - Date.now()) / 86400000);
}

function TaskCard({ task, members, onOpen, onMove, compact }) {
  const assignee = members?.find(m => m.id === task.assignee_id);
  const days     = daysUntilDue(task.due_date);
  const overdue  = days !== null && days < 0;
  const urgent   = days !== null && days >= 0 && days <= 2;
  const priStyle = PRI_COLOR[task.priority] || PRI_COLOR.Medium;

  return (
    <div
      draggable
      onDragStart={e => e.dataTransfer.setData("taskId", task.id)}
      onClick={() => onOpen?.(task)}
      className={`rounded-lg border bg-white/3 p-3 cursor-pointer hover:border-[#00ff90]/20 hover:bg-[#00ff90]/3 transition-all group select-none ${overdue ? "border-red-500/20" : "border-white/5"}`}>

      {/* Priority + overdue */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className={`text-xs px-2 py-0.5 rounded-full border ${priStyle}`}>{task.priority || "Medium"}</span>
        {overdue  && <AlertTriangle size={12} className="text-red-400 shrink-0" />}
        {urgent && !overdue && <Clock size={12} className="text-yellow-400 shrink-0" />}
      </div>

      <p className="text-sm text-white/75 font-medium leading-snug mb-2 group-hover:text-white transition-colors">
        {task.title}
      </p>

      {!compact && task.description && (
        <p className="text-xs text-white/30 mb-2 line-clamp-2">{task.description}</p>
      )}

      {/* Goal link badge */}
      {task.goal_id && (
        <p className="text-xs text-[#00ff90]/40 mb-2">⚡ Linked to goal</p>
      )}

      <div className="flex items-center justify-between gap-2">
        {assignee ? (
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[10px] text-white/50">
              {assignee.name?.[0]?.toUpperCase()}
            </div>
            <span className="text-xs text-white/35">{assignee.name?.split(" ")[0]}</span>
          </div>
        ) : <span className="text-xs text-white/20">Unassigned</span>}

        {days !== null && (
          <span className={`text-xs ${overdue ? "text-red-400" : urgent ? "text-yellow-400" : "text-white/25"}`}>
            {overdue ? `${Math.abs(days)}d overdue` : days === 0 ? "Today" : `${days}d`}
          </span>
        )}
      </div>
    </div>
  );
}

export default function KanbanBoard({ tasks = [], members = [], projects = [], onMove, onOpen, onCreate, compact }) {
  const [draggingOver, setDraggingOver] = useState(null);

  const byStatus = (status) => tasks.filter(t => t.status === status);

  const handleDrop = (e, status) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("taskId");
    if (id) onMove?.(id, status);
    setDraggingOver(null);
  };

  const cols = compact ? COLUMNS.filter(c => c.key !== "Backlog") : COLUMNS;

  return (
    <div className={`${card} overflow-hidden`}>
      <div className="px-5 py-4 border-b border-white/5">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Task Board</span>
      </div>
      <div className={`flex gap-3 p-4 overflow-x-auto ${compact ? "min-h-[280px]" : "min-h-[480px]"}`}>
        {cols.map(col => {
          const colTasks = byStatus(col.key);
          return (
            <div key={col.key}
              className={`flex flex-col rounded-xl border bg-white/2 transition-colors shrink-0 ${compact ? "w-56" : "w-64"} ${col.border} ${draggingOver === col.key ? "bg-[#00ff90]/5" : ""}`}
              onDragOver={e => { e.preventDefault(); setDraggingOver(col.key); }}
              onDragLeave={() => setDraggingOver(null)}
              onDrop={e => handleDrop(e, col.key)}>

              {/* Column header */}
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: col.color }} />
                  <span className="text-xs font-medium text-white/60">{col.label}</span>
                  <span className="text-xs text-white/25 bg-white/5 px-1.5 py-0.5 rounded-full">{colTasks.length}</span>
                </div>
                {!compact && onCreate && (
                  <button onClick={() => onCreate({ status: col.key })}
                    className="w-5 h-5 rounded flex items-center justify-center text-white/20 hover:text-[#00ff90] transition-colors">
                    <Plus size={13} />
                  </button>
                )}
              </div>

              {/* Tasks */}
              <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {colTasks.length === 0 ? (
                  <p className="text-white/15 text-xs italic text-center py-4">Drop here</p>
                ) : colTasks.map(t => (
                  <TaskCard key={t.id} task={t} members={members} onOpen={onOpen} onMove={onMove} compact={compact} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}